import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';

const mainDirections = [
  {
    id: 'business-finance',
    name: '商業理財',
    description: '出版與商業思考、管理、經濟及理財知識相關的閱讀內容。',
    icon: 'ri-line-chart-line',
    accent: 'bg-accent-100',
    iconColor: 'text-accent-700',
  },
  {
    id: 'politics',
    name: '政治',
    description: '出版與政治、社會、公共議題及時代觀察相關的閱讀內容。',
    icon: 'ri-scales-3-line',
    accent: 'bg-secondary-100',
    iconColor: 'text-secondary-700',
  },
  {
    id: 'history-humanities',
    name: '以古諷今與人文社科',
    description: '透過歷史事件、人物、思想與制度，連結並理解當代社會及人文議題。',
    icon: 'ri-history-line',
    accent: 'bg-accent-100',
    iconColor: 'text-accent-700',
  },
];

export default function PublishingDirectionsSection() {
  const { ref, inView } = useInView();

  const anim = (inView: boolean) =>
    `transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  return (
    <section className="min-h-[100svh] flex flex-col justify-center py-20 md:py-28 px-6 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto w-full">
        <div ref={ref} className={anim(inView)}>

          {/* ── 標題區 ── */}
          <div className="text-center mb-16">
            <p className="text-base font-label tracking-widest text-foreground-500 mb-3 uppercase">Directions</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              主要出版方向
            </h2>
            <p className="text-base text-foreground-500 max-w-3xl mx-auto leading-relaxed">
              山頂視角聚焦商業理財、政治與以古諷今類人文社科書籍，透過商業、歷史與公共議題的不同視角，提供理解社會與時代變化的出版內容。
            </p>
          </div>

          {/* ── 三大方向 ── */}
          <div className="space-y-5">
            {mainDirections.map((dir, i) => (
              <div
                key={dir.id}
                className="group flex flex-col md:flex-row gap-5 md:gap-8 bg-background-100 rounded-lg p-6 md:p-8 hover:bg-background-200/70 transition-colors duration-300"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Icon block */}
                <div className="shrink-0">
                  <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center ${dir.accent} rounded-lg`}>
                    <i className={`${dir.icon} text-2xl md:text-3xl ${dir.iconColor}`}></i>
                  </div>
                </div>

                {/* Text block */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                    {dir.name}
                  </h3>
                  <p className="text-base text-foreground-500 leading-relaxed">
                    {dir.description}
                  </p>
                </div>

                {/* Arrow hint */}
                <div className="hidden md:flex items-center shrink-0">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200/60 group-hover:bg-accent-100 transition-colors duration-300">
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-accent-700 transition-colors duration-300"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── 內容邊界免責聲明 ── */}
          <div className="mt-12 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-start gap-2 text-base text-foreground-400 leading-relaxed bg-background-100 rounded-lg px-5 py-4">
              <i className="ri-information-line text-foreground-400 mt-0.5 shrink-0"></i>
              <span>
                以上出版方向屬於書籍與知識閱讀內容。山頂視角不提供投資顧問、金融服務、政治顧問、選舉服務或政策諮詢。個別書籍及作者觀點，不代表山頂視角或崧博出版的官方政治立場。
              </span>
            </div>
          </div>

          {/* ── CTA 區 ── */}
          <div className="text-center mt-10">
            <Link
              to="/stores"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-label font-medium text-base rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              查看合作購書通路
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* ── 書籍資料庫入口 ── */}
          <div className="mt-16 pt-12 border-t border-foreground-200/40">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                查詢完整書目資訊
              </h2>
              <p className="text-base text-foreground-500 leading-relaxed mb-6">
                完整書名、作者、內容主題及相關出版資訊，請前往崧博書籍資料庫查詢。
              </p>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 text-white font-label font-medium text-base rounded-full hover:bg-secondary-600 transition-all duration-300 whitespace-nowrap"
              >
                前往書籍資料庫
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}